import { Box, InputAdornment, SxProps, Theme, Typography, useTheme } from "@mui/material";
import * as Icon from "akar-icons";
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { prefixClassName } from "@smartrr/shared/components/primitives";
import { SearchInput } from "@smartrr/shared/customerPortal/components/SearchInput";
import { ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";

interface ITitleAndSearchBarProps {
  /** String to be shown next to search bar */
  title?: string;

  /** Toggle for when `searchBar` is expanded - Please use in conjunction with `useMediaQuery`*/
  isMobile: boolean;

  /** Text inputted by user */
  subscribedPurchasablesSearchText: string;

  /** Setter for text inputted by user */
  updateSubscribedPurchasablesSearchText: (text: string) => void;

  /** Mui's `sx` prop for customizing wrapper styling
   * - [Learn More](https://mui.com/system/the-sx-prop/)
   */
  sx?: SxProps<Theme>;

  /**
   * Search debounce timing
   */
  debounceTime?: number;

  addClassName?: string;
}

const Wrapper = styled(Box).attrs(props => ({
  ...props,
  className: prefixClassName("title-search-wrapper"),
}))`
  display: flex;
  flex-direction: row;
  height: 30px;
  justify-content: space-between;
`;

export const TitleAndSearchBar = ({
  title,
  isMobile,
  subscribedPurchasablesSearchText,
  updateSubscribedPurchasablesSearchText,
  sx,
  addClassName,
  debounceTime = 100,
  ...props
}: ITitleAndSearchBarProps): JSX.Element => {
  const theme: ICustomerPortalTheme = useTheme();
  const searchRef = useRef(null);
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false);

  const debouncedSubscribedPurchasablesQuery = useCallback(
    debounce(term => {
      updateSubscribedPurchasablesSearchText(term.trim());
    }, debounceTime),
    []
  );

  const searchSubscribedPurchasablesCallback = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      e.persist();
      debouncedSubscribedPurchasablesQuery(e?.target?.value ?? "");
    },
    []
  );

  function ClickOutsideHandler(
    ref: React.MutableRefObject<any>,
    setter: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    useEffect(() => {
      function handleClickOutside(event: { target: any }) {
        if (ref.current && !ref.current.contains(event.target)!) {
          setter(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [ref]);
  }

  ClickOutsideHandler(searchRef, setSearchExpanded);

  return (
    <Wrapper className={addClassName} sx={{ ...sx }}>
      <Typography
        data-testid="searchbar-title"
        variant="h4"
        style={{ display: searchExpanded ? "none" : "block", whiteSpace: "nowrap" }}
      >
        {title}
      </Typography>
      <SearchInput
        data-testid="searchbar-input"
        ref={searchRef}
        defaultValue={subscribedPurchasablesSearchText}
        type="search"
        placeholder={typedLocalize("subscriptions.addToYourNextOrder.search")}
        onChange={searchSubscribedPurchasablesCallback}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon.Search size={12} color={theme.modernIconsColor} />
            </InputAdornment>
          ),
        }}
        onClick={isMobile ? () => setSearchExpanded(true) : null}
        searchExpanded={searchExpanded}
        isMobile={isMobile}
      />
    </Wrapper>
  );
};
